import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type {
  DateString,
  IanaTimezone,
  TimeString,
} from '#/types/TypedDateString';
import { shortDate } from '#/utils/string/datetime/dateString';
import { shortIanaTimezone } from '#/utils/string/datetime/ianaTimezone';
import { humanTime } from '#/utils/string/datetime/timeString';

type Args = {
  locale: I18nLocale;
  startDate: DateString;
  startTime: TimeString | null;
  endDate: DateString | null;
  endTime: TimeString | null;
  timezone: IanaTimezone | null;
};

/**
 * @example
 *
 * //=> Just one date, no time
 * 1/12/2024
 *
 * //=> Two dates, no time
 * 1/12/2024 - 4/02/2025
 *
 * //=> One date, single time
 * 1/12/2024 10:00 AM EDT
 *
 * //=> One date, time range
 * 1/12/2024 10:00 AM - 5:00 PM EDT
 *
 * //=> Two dates with time
 * 1/12/2024 10:00 AM - 4/02/2025 5:00 PM EDT
 */
export function formatSearchHitDate({
  locale,
  startDate,
  startTime,
  endDate,
  endTime,
  timezone,
}: Args) {
  const startString = [
    shortDate(startDate, { locale, yearFormat: 'long' }),
    startTime && humanTime(startTime, { locale }),
  ]
    .filter(Boolean)
    .join(' ');

  const endString = [
    endDate &&
      endDate !== startDate &&
      shortDate(endDate, { locale, yearFormat: 'long' }),
    endTime && humanTime(endTime, { locale }),
  ]
    .filter(Boolean)
    .join(' ');

  const combinedStartEndStrnig = [startString, endString]
    .filter(Boolean)
    .join(' - ');

  const timezoneString =
    (startTime || endTime) &&
    timezone &&
    shortIanaTimezone(timezone, { date: startDate, locale });

  return [combinedStartEndStrnig, timezoneString].filter(Boolean).join(' ');
}
